import { Box, Button, Grid2, Link, Tooltip, Typography } from "@mui/material";
import React, { useState } from "react";
import { ReactComponent as StreakIcon } from "Assets/Icons/Home/StreakIcon.svg";
import { ReactComponent as AttendanceStreak } from "Assets/Icons/Leaderboard/AttendanceStreak.svg";
import { ReactComponent as AttendanceFreeze } from "Assets/Icons/Leaderboard/AttendanceFreeze.svg";
import { ReactComponent as AlertIcon } from "Assets/Icons/Leaderboard/AlertIcon.svg";
import { ReactComponent as CoinIcon } from "Assets/Icons/Home/CoinIcon.svg";
import { ReactComponent as StarIcon } from "Assets/Icons/Leaderboard/StarIcon.svg";
import PrimaryIcon from "Components/Common/PrimaryIcon";
import PrimarySlider from "Components/Common/PrimarySlider";
import { SwiperSlide } from "swiper/react";
import Warning from "Components/Common/Warning";
import AttendanceStreakDialog from "./AttendanceStreakDialog";

const styles = (theme) => ({
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  "& .streakBox": {
    borderRadius: "8px",
    padding: "16px",
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  "& .weeklyStreakRoot": {
    border: `1px solid ${theme.palette.border.blue}`,
    borderRadius: "8px",
    padding: "12px 16px",
  },
  "& .attendanceBox": {
    border: `1px solid ${theme.palette.border.blue}`,
    borderRadius: "8px",
    background: theme.palette.background.default,
    padding: "16px",
    height: "140px",
    display: "flex",
    alignItems: "center",
    flexDirection: "column",
    justifyContent: "center",
    gap: "10px",
    "& svg": {
      padding: "2px",
      background: theme.palette.white.main,
      borderRadius: "50px",
    },
  },
  "& .active": {
    background: theme.palette.primary.main,
    color: theme.palette.white.main,
  },
});

const AttendanceSection = () => {
  const [showAttendanceStreak, setShowAttendanceStreak] = useState(false);

  const streakList = [
    {
      label: "Best Attendance Streak",
      value: 8,
      icon: AttendanceStreak,
      background: "#F0FFF8",
      border: "#E0E9FB",
    },
    {
      label: "Current Streak",
      value: 0,
      icon: StreakIcon,
      background: "#FFF7E8",
      border: "#E3AF51",
    },
    {
      label: (
        <Box display={"flex"} alignItems={"center"} gap="10px">
          {" "}
          Streak Freeze Left{" "}
          <Tooltip
            arrow
            followCursor
            title="Extra protection for your streak if you miss to attend a live class / recording."
          >
            {" "}
            <PrimaryIcon cursor="pointer" icon={AlertIcon} />{" "}
          </Tooltip>
        </Box>
      ),
      value: (
        <Box display={"flex"} alignItems={"center"} gap="10px">
          0{" "}
          <Link
            href="#"
            onClick={() => {}}
            fontWeight={500}
            underline="none"
            variant="subtitle1"
          >
            {" "}
            Buy More
          </Link>
        </Box>
      ),
      icon: AttendanceFreeze,
      background: "#F1EDFF",
      border: "#BFB0F5",
    },
  ];

  return (
    <>
      <Grid2 sx={styles}>
        <Box
          display={"flex"}
          alignItems={"center"}
          justifyContent={"space-between"}
        >
          <Typography fontWeight={500} variant="title" display={"flex"}>
            Attendance Streak
          </Typography>
          <Button
            onClick={() => setShowAttendanceStreak(true)}
            variant="outlined"
            color="secondary"
          >
            Attendance Streaks
          </Button>
        </Box>
        <Grid2
          container
          spacing={2}
          columns={{ xl: 12, lg: 12, md: 12, sm: 4 }}
        >
          {streakList?.map((item, i) => {
            return (
              <Grid2
                size={4}
                className="streakBox"
                sx={{
                  background: item?.background,
                  border: `1px solid ${item?.border}`,
                }}
              >
                <Typography variant="subtitle" fontWeight={500}>
                  {item?.label}
                </Typography>
                <Box
                  display={"flex"}
                  alignItems={"center"}
                  justifyContent={"space-between"}
                >
                  <Typography fontWeight={600} fontSize={28} lineHeight={1}>
                    {item?.value}
                  </Typography>
                  <PrimaryIcon icon={item?.icon} height="30px" width="30px" />
                </Box>
              </Grid2>
            );
          })}
        </Grid2>
        <Grid2 className="weeklyStreakRoot">
          <Box display={"flex"} alignItems={"center"} gap="8px" mb="16px">
            <Typography fontWeight={500} variant="subtitle">
              Weekly Streak Goal
            </Typography>
            <Tooltip
              arrow
              followCursor
              title="Explore the rewards you can earn by attending live classes and watching recordings."
            >
              {" "}
              <PrimaryIcon cursor="pointer" icon={AlertIcon} />{" "}
            </Tooltip>
          </Box>
          <PrimarySlider
            slidesPerView={{
              1200: { slidesPerView: 9 },
              900: { slidesPerView: 6 },
              600: { slidesPerView: 4 },
              0: { slidesPerView: 2 },
            }}
            spacing={"15px"}
          >
            {[...Array(10)]?.map((item, i) => {
              return (
                <SwiperSlide>
                  <Grid2
                    key={`attendance${i}`}
                    className={`attendanceBox ${i == 1 ? "active" : ""}`}
                  >
                    <Typography variant="title" fontWeight={500}>
                      4 Nov
                    </Typography>
                    <PrimaryIcon height="30px" width="30px" icon={StreakIcon} />
                  </Grid2>{" "}
                </SwiperSlide>
              );
            })}
          </PrimarySlider>
        </Grid2>
        <Box
          sx={(theme) => ({
            background: theme.palette.background.default,
            borderRadius: "8px",
            padding: "10px 16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          })}
        >
          <Typography
            display={"flex"}
            alignItems={"center"}
            gap="10px"
            variant="subtitle"
            fontWeight={500}
          >
            <PrimaryIcon height="22px" width="22px" icon={StarIcon} />
            Woah, you are a streak superstar! Continue the good work and earn 8
            coded coins
            <PrimaryIcon height="22px" width="22px" icon={CoinIcon} />
          </Typography>
          <Button variant="contained">Join Live Class</Button>
        </Box>
        <Warning showAlert={false}>
          <Typography width={"100%"} textAlign={"center"}>
            Learners with 2 digit streak have better career outcomes 🎉
          </Typography>
        </Warning>
      </Grid2>
      <AttendanceStreakDialog
        open={showAttendanceStreak}
        handleClose={() => setShowAttendanceStreak(false)}
      />
    </>
  );
};

export default AttendanceSection;
